import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  ApplicantsExamsFilesResponse,
  DeleteApplicantsExamsFilesPayload,
  deleteApplicantsExamsFiles,
  getApplicantsExamsFiles,
} from "@api/applicants";

import { useStoreContext } from "@context";

import { getLastWeekTimestamp } from "@shared/services/date";
import { FileUploadStatus } from "@shared/services/fileUpload";
import Message from "@shared/services/message";
import { formatBytes } from "@shared/services/string";

export interface ApplicantsExamsFiles extends ApplicantsExamsFilesResponse {
  displaySize: string;
  file?: File;
  cardStatus: {
    isError?: boolean;
    isRemove?: boolean;
    isRetry?: boolean;
    isUploading?: boolean;
  };
  status: FileUploadStatus;
  statusMessage?: string;
  tooltip: string;
}

interface UseRemoveApplicantsExamFileListParams {
  applicantExamId: number;
  resultId: number;
  data: DeleteApplicantsExamsFilesPayload;
}

export const applicantsExamsFilesKeys = {
  all: ["applicants", "examFiles"] as const,
  examFileList: (applicantExamId: number, resultId: number) => {
    if (!resultId) {
      return [...applicantsExamsFilesKeys.all, applicantExamId] as const;
    }

    return [
      ...applicantsExamsFilesKeys.all,
      applicantExamId,
      resultId,
    ] as const;
  },
};

export function useApplicantsExamsFileList(resultId: number) {
  const { applicantExamId } = useStoreContext();
  const enabled = Boolean(applicantExamId && resultId);

  const query = useQuery({
    queryKey: applicantsExamsFilesKeys.examFileList(applicantExamId, resultId),
    queryFn: async ({ signal }) => {
      const { result } = await getApplicantsExamsFiles({
        applicantExamId,
        resultId,
        options: {
          signal,
        },
      });

      return result;
    },
    enabled,
    initialData: [] as ApplicantsExamsFiles[],
    initialDataUpdatedAt: getLastWeekTimestamp(),
    staleTime: 0,
    select: (data) =>
      data.map((item) => ({
        ...item,
        id: item.filename,
        displaySize: formatBytes(item.sizeInBytes),
        cardStatus: { isRemove: true },
        status: FileUploadStatus.COMPLETE,
        statusMessage: undefined,
        tooltip: Message.getMessageByKey("challenge.removed"),
      })),
  });

  return query;
}

export function useRemoveApplicantsExamFileList() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ data, ...rest }: UseRemoveApplicantsExamFileListParams) =>
      deleteApplicantsExamsFiles({ ...rest, options: { data } }),
    onSuccess: (_, { applicantExamId, resultId, data: { filenames } }) => {
      toast.success(Message.getMessageByKey("fileUploader.remove"));
      const fileNameMap = filenames.reduce((acc, name) => {
        acc[name] = true;
        return acc;
      }, {});
      client.setQueryData(
        applicantsExamsFilesKeys.examFileList(applicantExamId, resultId),
        (oldData: ApplicantsExamsFiles[]) =>
          oldData.filter(({ filename }) => !Boolean(fileNameMap[filename])),
      );
    },
    onError: () => {
      toast.error(Message.getMessageByKey("fileUploader.removeError"));
    },
  });

  return mutation;
}
